/**
 * Rumi context planning (spec section 11): focused-answer retrieval planning (query terms → FTS
 * query + deterministic filters), Full Review batching with coverage tracking (A17), the citation
 * model, the "not available in this snapshot" decision, and validated tool input schemas.
 */
import { z } from 'zod';
import type { DatasetId, ObjectType, RecordKey, SnapshotId } from '../../ids.js';
import { OBJECT_TYPES, RecordKeySchema, SnapshotIdSchema, DatasetIdSchema, ObjectTypeSchema } from '../../ids.js';
import type { OpenAIFunctionTool, RumiToolName } from '../../providers/openai.js';
import type { CoverageManifest, RumiCitation, RumiCoverage, SourceRecord } from '../../providers/records.js';
import { SEVERITY_LEVELS, STANDARD_STATUSES, StandardStatusSchema, SeverityLevelSchema, type SeverityLevel, type StandardStatus } from '../../status.js';

// ---------------------------------------------------------------------------------------------
// Focused retrieval planning
// ---------------------------------------------------------------------------------------------

export interface RetrievalFilters {
  statuses: StandardStatus[];
  objectTypes: ObjectType[];
  severities: SeverityLevel[];
  frameworkHints: string[];
  failingOnly: boolean;
}

export interface RetrievalPlan {
  terms: string[];
  /** FTS5 query requiring every term (precision). */
  strictQuery: string;
  /** FTS5 query matching any term (recall fallback). */
  looseQuery: string;
  filters: RetrievalFilters;
  /** Deterministic aggregate the runner should compute in code before asking the model. */
  aggregates: ('countByStatus' | 'countBySeverity')[];
  limit: number;
}

const STOPWORDS = new Set(['a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'do', 'does', 'for', 'from', 'has', 'have', 'how', 'i', 'in', 'is', 'it', 'its', 'list', 'many', 'me', 'my', 'of', 'on', 'or', 'our', 'show', 'that', 'the', 'their', 'there', 'these', 'this', 'to', 'us', 'we', 'what', 'which', 'who', 'with', 'you', 'all', 'any', 'about', 'tell', 'give', 'please', 'currently', 'current']);

const STATUS_HINTS: [RegExp, StandardStatus[]][] = [
  [/\b(failing|failed|fail|not passing|open findings?|broken)\b/i, ['FAIL', 'NEEDS_ATTENTION', 'DUE_SOON']],
  [/\b(needs? attention|attention)\b/i, ['NEEDS_ATTENTION']],
  [/\b(due soon|expiring|expires? soon)\b/i, ['DUE_SOON']],
  [/\b(passing|passed|compliant|ok)\b/i, ['PASS']],
  [/\b(unknown|unclear|missing data)\b/i, ['UNKNOWN']],
  [/\b(deactivated|disabled|archived)\b/i, ['DEACTIVATED']],
  [/\b(not applicable|n\/a)\b/i, ['NOT_APPLICABLE']],
];

const OBJECT_HINTS: [RegExp, ObjectType[]][] = [
  [/\b(polic(y|ies))\b/i, ['policy', 'document']],
  [/\b(documents?|evidence)\b/i, ['document', 'evidence']],
  [/\b(tests?)\b/i, ['test']],
  [/\b(controls?)\b/i, ['control']],
  [/\b(monitors?)\b/i, ['monitor', 'monitor_failure']],
  [/\b(vulnerabilit(y|ies)|cves?|findings?|packages?)\b/i, ['vulnerability', 'monitor_failure']],
  [/\b(people|person|personnel|employees?|staff|users?|contractors?)\b/i, ['person']],
  [/\b(devices?|computers?|laptops?|macs?|workstations?)\b/i, ['computer']],
  [/\b(integrations?|connected apps?)\b/i, ['integration']],
  [/\b(vendors?|suppliers?|third[- ]part(y|ies))\b/i, ['vendor']],
  [/\b(frameworks?|soc ?2|iso ?27001|hipaa|pci|gdpr|nist)\b/i, ['framework']],
];

const FRAMEWORK_HINTS: [RegExp, string][] = [
  [/\bsoc ?2\b/i, 'SOC 2'],
  [/\biso ?27001\b/i, 'ISO 27001'],
  [/\bhipaa\b/i, 'HIPAA'],
  [/\bpci(-| )?dss\b|\bpci\b/i, 'PCI DSS'],
  [/\bgdpr\b/i, 'GDPR'],
  [/\bnist\b/i, 'NIST'],
];

export function tokenizeQuestion(question: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of question.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').split(/[^a-z0-9@._-]+/)) {
    const t = raw.replace(/^[._-]+|[._-]+$/g, '');
    if (t.length < 2 || STOPWORDS.has(t) || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

/** Quote every term for FTS5 so punctuation and reserved words cannot alter the query. */
function ftsTerm(term: string): string {
  return `"${term.replace(/"/g, '""')}"`;
}

export function planFocusedRetrieval(question: string, options: { limit?: number } = {}): RetrievalPlan {
  const terms = tokenizeQuestion(question);
  const statuses = new Set<StandardStatus>();
  for (const [re, list] of STATUS_HINTS) if (re.test(question)) for (const s of list) statuses.add(s);
  const objectTypes = new Set<ObjectType>();
  for (const [re, list] of OBJECT_HINTS) if (re.test(question)) for (const o of list) objectTypes.add(o);
  const severities = SEVERITY_LEVELS.filter((s) => new RegExp(`\\b${s}\\b`, 'i').test(question));
  const frameworkHints = FRAMEWORK_HINTS.filter(([re]) => re.test(question)).map(([, name]) => name);
  const failingOnly = /\b(failing|failed|open|outstanding|not passing|broken)\b/i.test(question);
  const aggregates: RetrievalPlan['aggregates'] = [];
  if (/\b(how many|count|number of|total)\b/i.test(question)) {
    aggregates.push('countByStatus');
    if (/\b(sever|critical|high|medium|low)\b/i.test(question)) aggregates.push('countBySeverity');
  }
  const searchTerms = terms.filter((t) => !STANDARD_STATUSES.some((s) => s.toLowerCase().replace('_', ' ') === t));
  return {
    terms: searchTerms,
    strictQuery: searchTerms.map(ftsTerm).join(' AND '),
    looseQuery: searchTerms.map(ftsTerm).join(' OR '),
    filters: { statuses: [...statuses], objectTypes: [...objectTypes], severities: [...severities], frameworkHints, failingOnly },
    aggregates,
    limit: options.limit ?? 40,
  };
}

// ---------------------------------------------------------------------------------------------
// Full Review batching + coverage
// ---------------------------------------------------------------------------------------------

/** Rough token estimate: 1 token ≈ 4 characters of the serialised record fields Rumi sees. */
export function estimateRecordTokens(record: Pick<SourceRecord, 'title' | 'description' | 'status' | 'rawStatus' | 'objectType' | 'remediation' | 'recordKey'>): number {
  const text = [record.recordKey, record.objectType, record.title, record.description ?? '', record.status, record.rawStatus ?? '', record.remediation ?? ''].join(' ');
  return Math.max(8, Math.ceil(text.length / 4) + 12);
}

export interface BatchPlanInput {
  recordKey: RecordKey;
  estimatedTokens: number;
}

export interface ReviewBatch {
  index: number;
  recordKeys: RecordKey[];
  estimatedTokens: number;
}

export interface BatchPlan {
  batches: ReviewBatch[];
  totalRecords: number;
  totalEstimatedTokens: number;
  /** Records too large for a single batch are still scheduled alone and flagged. */
  oversizedRecordKeys: RecordKey[];
}

export interface BatchLimits {
  maxTokensPerBatch: number;
  maxRecordsPerBatch: number;
}

export const DEFAULT_BATCH_LIMITS: BatchLimits = { maxTokensPerBatch: 24_000, maxRecordsPerBatch: 200 };

/** Partition the selected scope into bounded batches, preserving input order for stable coverage. */
export function planFullReviewBatches(records: readonly BatchPlanInput[], limits: BatchLimits = DEFAULT_BATCH_LIMITS): BatchPlan {
  const batches: ReviewBatch[] = [];
  const oversized: RecordKey[] = [];
  let current: ReviewBatch | null = null;
  let totalTokens = 0;
  for (const r of records) {
    totalTokens += r.estimatedTokens;
    if (r.estimatedTokens > limits.maxTokensPerBatch) oversized.push(r.recordKey);
    const fits = current !== null && current.recordKeys.length < limits.maxRecordsPerBatch && current.estimatedTokens + r.estimatedTokens <= limits.maxTokensPerBatch;
    if (!fits) {
      current = { index: batches.length, recordKeys: [], estimatedTokens: 0 };
      batches.push(current);
    }
    current!.recordKeys.push(r.recordKey);
    current!.estimatedTokens += r.estimatedTokens;
  }
  return { batches, totalRecords: records.length, totalEstimatedTokens: totalTokens, oversizedRecordKeys: oversized };
}

export interface CoverageTracker {
  total: number;
  processed: number;
  processedBatchIndexes: number[];
  stopReason: RumiCoverage['unprocessedReason'];
}

export function createCoverageTracker(total: number): CoverageTracker {
  return { total, processed: 0, processedBatchIndexes: [], stopReason: 'none' };
}

export function markBatchProcessed(tracker: CoverageTracker, batch: ReviewBatch): CoverageTracker {
  if (tracker.processedBatchIndexes.includes(batch.index)) return tracker;
  return { ...tracker, processed: Math.min(tracker.total, tracker.processed + batch.recordKeys.length), processedBatchIndexes: [...tracker.processedBatchIndexes, batch.index] };
}

export function stopCoverage(tracker: CoverageTracker, reason: Exclude<RumiCoverage['unprocessedReason'], 'none'>): CoverageTracker {
  return { ...tracker, stopReason: reason };
}

/** Final coverage: partial whenever processed < total; the reason states why the rest was skipped. */
export function finalizeCoverage(tracker: CoverageTracker): RumiCoverage {
  const partial = tracker.processed < tracker.total;
  return { processed: tracker.processed, total: tracker.total, partial, unprocessedReason: partial ? (tracker.stopReason === 'none' ? 'provider_failure' : tracker.stopReason) : 'none' };
}

/** Text the answer must carry when coverage is partial (spec: never truncate and claim completeness). */
export function coverageStatement(coverage: RumiCoverage): string {
  if (!coverage.partial) return `Reviewed all ${coverage.total} selected records.`;
  const unprocessed = coverage.total - coverage.processed;
  const reason: Record<RumiCoverage['unprocessedReason'], string> = {
    budget: 'the budget limit was reached',
    context_size: 'the context size limit was reached',
    provider_failure: 'the provider request failed',
    cancelled: 'the review was cancelled',
    none: 'processing stopped',
  };
  return `Partial review: ${coverage.processed} of ${coverage.total} selected records were reviewed; ${unprocessed} were not processed because ${reason[coverage.unprocessedReason]}.`;
}

// ---------------------------------------------------------------------------------------------
// Citations
// ---------------------------------------------------------------------------------------------

export function buildCitation(record: Pick<SourceRecord, 'recordKey' | 'title' | 'objectType'>, snapshotId: SnapshotId): RumiCitation {
  return { recordKey: record.recordKey, label: record.title, snapshotId, objectType: record.objectType, expired: false };
}

/** Citations whose snapshot was removed by retention are kept but marked expired. */
export function markExpiredCitations(citations: readonly RumiCitation[], retainedSnapshotIds: ReadonlySet<SnapshotId>): RumiCitation[] {
  return citations.map((c) => (retainedSnapshotIds.has(c.snapshotId) ? c : { ...c, expired: true }));
}

/** Extract record keys the model cited in its answer (only keys that were actually in context count). */
export function extractCitedKeys(answer: string, contextKeys: ReadonlySet<RecordKey>): RecordKey[] {
  const found = new Set<RecordKey>();
  for (const key of contextKeys) if (answer.includes(key)) found.add(key);
  return [...found].sort();
}

// ---------------------------------------------------------------------------------------------
// Not available in this snapshot
// ---------------------------------------------------------------------------------------------

export type NotAvailableReason = 'dataset_not_synced' | 'access_unavailable' | 'sync_incomplete' | 'field_unsupported' | 'no_matching_records';

export interface AvailabilityDecision {
  available: boolean;
  reason: NotAvailableReason | null;
  statement: string | null;
}

/**
 * Decide whether evidence for a question exists in the snapshot. A stale dataset or a missing
 * provider field is never grounds for inventing an answer.
 */
export function decideAvailability(input: { matches: number; datasetIds: readonly DatasetId[]; coverage: CoverageManifest | null; fieldUnsupported?: boolean }): AvailabilityDecision {
  if (input.fieldUnsupported) return { available: false, reason: 'field_unsupported', statement: 'Not available in this snapshot: the provider does not expose this field.' };
  if (input.matches > 0) return { available: true, reason: null, statement: null };
  if (!input.coverage) return { available: false, reason: 'dataset_not_synced', statement: 'Not available in this snapshot: no synced data.' };
  for (const id of input.datasetIds) {
    const ds = input.coverage.datasets.find((d) => d.id === id);
    if (!ds || ds.state === 'unsynced') {
      const reason = (ds?.reason ?? '').toLowerCase();
      if (/permission|forbidden|unauthori|unsupported|access|scope|terms/.test(reason)) return { available: false, reason: 'access_unavailable', statement: `Not available in this snapshot: access to ${id} is unavailable for this connection.` };
      return { available: false, reason: 'dataset_not_synced', statement: `Not available in this snapshot: ${id} has not been synced.` };
    }
    if (!ds.complete) return { available: false, reason: 'sync_incomplete', statement: `Not available in this snapshot: ${id} is incomplete in this snapshot.` };
  }
  return { available: false, reason: 'no_matching_records', statement: 'Not available in this snapshot: no matching records.' };
}

// ---------------------------------------------------------------------------------------------
// Tool input schemas
// ---------------------------------------------------------------------------------------------

const limit = z.number().int().min(1).max(200).default(40);

export const SearchRecordsInputSchema = z.object({
  query: z.string().min(1).max(500),
  objectTypes: z.array(ObjectTypeSchema).max(OBJECT_TYPES.length).optional(),
  statuses: z.array(StandardStatusSchema).optional(),
  severities: z.array(SeverityLevelSchema).optional(),
  frameworkId: z.string().max(200).optional(),
  limit,
  cursor: z.string().max(200).optional(),
});
export const GetRecordInputSchema = z.object({ recordKey: RecordKeySchema, includeChildren: z.boolean().default(true) });
export const GetRelatedFindingsInputSchema = z.object({ recordKey: RecordKeySchema, relation: z.enum(['children', 'parent', 'same_framework', 'same_owner', 'same_asset']).default('children'), limit });
export const CountByStatusInputSchema = z.object({ objectTypes: z.array(ObjectTypeSchema).optional(), frameworkId: z.string().max(200).optional(), groupBy: z.enum(['status', 'severity', 'objectType']).default('status') });
export const CompareSnapshotsInputSchema = z.object({ fromSnapshotId: SnapshotIdSchema, toSnapshotId: SnapshotIdSchema, datasetIds: z.array(DatasetIdSchema).optional(), limit: z.number().int().min(1).max(500).default(100) });
export const GetCoverageManifestInputSchema = z.object({ snapshotId: SnapshotIdSchema.nullable().optional() });

export const RUMI_TOOL_INPUT_SCHEMAS: Record<RumiToolName, z.ZodType> = {
  searchRecords: SearchRecordsInputSchema,
  getRecord: GetRecordInputSchema,
  getRelatedFindings: GetRelatedFindingsInputSchema,
  countByStatus: CountByStatusInputSchema,
  compareSnapshots: CompareSnapshotsInputSchema,
  getCoverageManifest: GetCoverageManifestInputSchema,
};

export const RUMI_TOOL_DESCRIPTIONS: Record<RumiToolName, string> = {
  searchRecords: 'Full-text search over the selected snapshot of the active account. Returns record keys, titles, statuses and short descriptions.',
  getRecord: 'Fetch one record by key, including its children summary, links and effective ask.',
  getRelatedFindings: 'Records related to a record: children, parent, same framework, same owner or same asset.',
  countByStatus: 'Deterministic counts grouped by status, severity or object type. Use this for any numeric answer.',
  compareSnapshots: 'Added, removed and changed records between two retained snapshots of this account.',
  getCoverageManifest: 'Which datasets the snapshot covers completely; use before claiming something is absent.',
};

export type RumiToolInput<N extends RumiToolName> = z.infer<(typeof RUMI_TOOL_INPUT_SCHEMAS)[N]>;

/** Validate model-authored tool arguments; account scope is enforced by the runner, not here. */
export function parseRumiToolInput<N extends RumiToolName>(tool: N, raw: unknown): { ok: true; input: RumiToolInput<N> } | { ok: false; error: string } {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return { ok: false, error: 'arguments are not valid JSON' };
    }
  }
  const parsed = RUMI_TOOL_INPUT_SCHEMAS[tool].safeParse(value);
  if (!parsed.success) return { ok: false, error: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') };
  return { ok: true, input: parsed.data as RumiToolInput<N> };
}

/** OpenAI function tool definitions generated from the zod schemas. */
export function rumiToolDefinitions(names: readonly RumiToolName[] = Object.keys(RUMI_TOOL_INPUT_SCHEMAS) as RumiToolName[]): OpenAIFunctionTool[] {
  return names.map((name) => {
    const schema = z.toJSONSchema(RUMI_TOOL_INPUT_SCHEMAS[name], { io: 'input', target: 'draft-7' }) as Record<string, unknown>;
    delete schema.$schema;
    return { type: 'function', name, description: RUMI_TOOL_DESCRIPTIONS[name], parameters: schema, strict: false };
  });
}
